import { api, extractListItems, extractPaging, type ApiResponse } from '../client';

export enum DonorTypeEnum {
    EMPLOYEE = 'employee',
    EMPLOYER = 'employer'
}

export interface Donor {
    donorType: string;
    donorId: string;
    donorName: string;
    /** Present for employee donors (donorType=employee) */
    donorEmail?: string | null;
    donationCount: number;
    totalValueCents: number;
    firstDonation: string | null;
    lastDonation: string | null;
}

export interface DonorFilters {
    donorType: string;
    donorId?: string;
    search?: string;
    start_date?: string;
    end_date?: string;
    sort_by?: 'type' | 'name' | 'value' | 'first' | 'last';
    sort_direction?: 'asc' | 'desc';
    page?: number;
    per_page?: number;
    employer?: string;
    status?: string;
}

export interface PaginationInfo {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
}

export interface DonorsResponse {
    success: boolean;
    data: Donor[];
    paging: PaginationInfo;
    message?: string;
}

export interface ApiSuccessResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface DonationHistoryItem {
    uuid: string;
    date: string;
    leaveHours: number;
    leaveValueCents: number;
    rejectedAt: string | null;
    rejectionReason: string | null;
    status: {
        uuid: string;
        name: string;
        totalValueCents: number;
    };
    charity?: {
        uuid: string;
        name: string;
    };
    donor?: {
        type: string;
        uuid: string;
        name: string;
        email: string;
    };
    employerTopUpCents?: number | null;
    approvedAt: string | null;  
    employer?: {
        uuid: string;
        name: string;
        full_name: string;
        email: string;
    };
    user?: {
        uuid: string;
        name: string;
    };
    totalValueCents: number;
}

export interface DonationHistoryResponse {
    success: boolean;
    data: DonationHistoryItem[];
    message?: string;
}

// API base URL
const API_BASE_URL = '/donors';

export const DonorService = {
    /**
     * Get all donors with filters and pagination
     */
    getDonors: async (filters: DonorFilters): Promise<{
        donors?: Donor[];
        paging?: PaginationInfo;
        success: boolean;
        error?: any;
    }> => {
        try {
            // Build query parameters
            const queryParams = new URLSearchParams();
            
            // Required parameter
            queryParams.append('donorType', filters.donorType);
            
            if (filters.status) {
                queryParams.append('donation_request_status_uuid', filters.status);
            }

          
            
            if (filters.search) {
                queryParams.append('phrase', filters.search);
            }
            
            if (filters.start_date) {
                queryParams.append('start_date', filters.start_date);
            }
            
            if (filters.end_date) {
                queryParams.append('end_date', filters.end_date);
            }
            
            if (filters.employer) {
                queryParams.append('donorId', filters.employer);
            }
            
            if (filters.sort_by) {
                queryParams.append('sort_by', filters.sort_by);
            }
            
            if (filters.sort_direction) {
                queryParams.append('sort_direction', filters.sort_direction);
            }
            
            queryParams.append('page', filters.page?.toString() || '1');
            queryParams.append('per_page', filters.per_page?.toString() || '25');
            
            // Make API request
            const url = `${API_BASE_URL}?${queryParams.toString()}`;
            const response = await api.get<DonorsResponse>(url);
            
            if (response.data && response.data.success) {
                const items = extractListItems<Donor>(response.data.data);
                const rawPaging = extractPaging(response.data.data) ?? response.data.paging;
                const paging: PaginationInfo | undefined = rawPaging
                    ? {
                        current_page: rawPaging.current_page,
                        per_page: rawPaging.per_page,
                        total: rawPaging.total,
                        total_pages: 'last_page' in rawPaging ? rawPaging.last_page : rawPaging.total_pages,
                    }
                    : undefined;
                return {
                    donors: items,
                    paging,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to fetch donors'
                };
            }
        } catch (error) {
            console.error('Error fetching donors:', error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get a single donor by ID
     */
    getDonor: async (donorType: string, donorId: string): Promise<{
        donor?: Donor;
        success: boolean;
        error?: any;
    }> => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('donorType', donorType);
            queryParams.append('donorId', donorId);
            
            const url = `${API_BASE_URL}?${queryParams.toString()}`;
            const response = await api.get<DonorsResponse>(url);
            
            const list = extractListItems<Donor>(response.data?.data);
            if (response.data && response.data.success && list.length > 0) {
                return {
                    donor: list[0],
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Donor not found'
                };
            }
        } catch (error) {
            console.error(`Error fetching donor ${donorId}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get donation history for a specific donor
     */
    getDonationHistory: async (donorType: string, donorId: string, page: number = 1, perPage: number = 25): Promise<{
        donations?: DonationHistoryItem[];
        paging?: PaginationInfo;
        success: boolean;
        error?: any;
    }> => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('donorType', donorType);
            queryParams.append('donorId', donorId);
            queryParams.append('page', page.toString());
            queryParams.append('per_page', perPage.toString());
            
            const url = `/donors/history?${queryParams.toString()}`;
            const response = await api.get<DonationHistoryResponse>(url);
            
            if (response.data && response.data.success) {
                const items = extractListItems<DonationHistoryItem>(response.data.data);
                return {
                    donations: items,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to fetch donation history'
                };
            }
        } catch (error) {
            console.error(`Error fetching donation history for ${donorId}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Download PDF receipt for a specific donation request
     */
    downloadDonationReceipt: async (donationUuid: string): Promise<{
        success: boolean;
        error?: any;
    }> => {
        try {
            const url = `/donation-request/${donationUuid}/pdf`;
            
            // Since backend returns direct PDF file, we need to handle blob response
            const response = await api.get(url, {
                responseType: 'blob'
            });
            
            if (response.data) {
                // Create a download link - response.data should already be a blob
                const downloadUrl = window.URL.createObjectURL(response.data as Blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `donation-receipt-${donationUuid}.pdf`;
                
                // Trigger the download
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
                
                return {
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: 'No PDF data received'
                };
            }
        } catch (error) {
            console.error('Error downloading donation receipt:', error);
            return {
                success: false,
                error
            };
        }
    }
}; 